import React from 'react'
import * as rn from 'react-native'

import * as c from '../../common'
import * as r from '../../react-utils'

import Feather from 'react-native-vector-icons/Feather'
// import Feather from 'react-native-vector-icons/Feather'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'

import * as gs from '../gStyles'

import Labeled from './Labeled'

export interface NumberInputProps {
  allowDecimals?: boolean
  disabled?: boolean
  grow?: boolean
  guideline?: string
  guidelineType?: c.Guideline['type']
  hide?: boolean
  label?: false | string
  on: c.Surface
  onChange: (newValue: number) => void
  value?: number
}

export default React.memo<NumberInputProps>(function NumberInput({
  allowDecimals,
  disabled,
  grow,
  guideline,
  guidelineType,
  hide,
  label,
  on,
  onChange,
  value = 0,
}) {
  //#region styling
  const t = r.useTheme()
  const styles = gs.useThemedStyleSheet(themedStyles, on)
  //#endregion

  const [manualInputVal, manualInputSet] = React.useState('')
  const manualInputHandle = React.useCallback(
    (newVal: string) => {
      if (allowDecimals && c.stringIsFloating(newVal)) {
        const decimals = newVal.split('.')[1]?.length ?? 0
        if (decimals <= 2) {
          manualInputSet(newVal)
        }
      } else if (c.stringIsInt(newVal)) {
        manualInputSet(newVal)
      }
    },
    [allowDecimals],
  )
  React.useEffect(() => {
    if (allowDecimals) {
      manualInputSet(value.toFixed(2))
    } else {
      manualInputSet(value.toString())
    }
  }, [allowDecimals, value])
  const [
    manualInputEnabledVal,
    manualInputEnabledSet,
    manualInputEnabledToggle,
  ] = r.useBool(false)
  const manualInputRef = React.useRef<rn.TextInput>(null)
  React.useEffect(
    // Cannot be done inside manualInputFocus because the ref is not set yet
    // when that function is called
    function inputFocusOnReady() {
      if (manualInputEnabledVal) {
        manualInputRef.current?.focus()
      }
    },
    [manualInputEnabledVal],
  )
  const manualInputDiscard = React.useCallback(() => {
    manualInputRef.current?.blur()
    if (allowDecimals) {
      manualInputSet(value.toFixed(2))
    } else {
      manualInputSet(value.toString())
    }
    manualInputEnabledSet(false)
  }, [allowDecimals, manualInputEnabledSet, value])
  const manualInputConfirm = React.useCallback(() => {
    manualInputRef.current?.blur()
    // parseFloating() works for integers too
    onChange(c.parseFloating(manualInputVal))
    manualInputEnabledSet(false)
  }, [manualInputEnabledSet, manualInputVal, onChange])

  const handleMinus = React.useCallback(() => {
    if (allowDecimals && value >= 0) {
      // A weird error happens when sometimes the math comes out to an excessive
      // number of decimal places for some reason, so we restrict to 2 places
      const res = value - 1
      const roundedStr = res.toFixed(2)
      const roundedNum = c.parseFloating(roundedStr)
      const forcePositive = Math.max(0, roundedNum)
      onChange(forcePositive)
    }
    if (!allowDecimals && value > 0) {
      onChange(value - 1)
    }
  }, [allowDecimals, onChange, value])
  const handleMinusPointOne = React.useCallback(() => {
    if (allowDecimals && value >= 0) {
      // A weird error happens when sometimes the math comes out to an excessive
      // number of decimal places for some reason, so we restrict to 2 places
      const res = value - 0.1
      const roundedStr = res.toFixed(2)
      const roundedNum = c.parseFloating(roundedStr)
      const forcePositive = Math.max(0, roundedNum)
      onChange(forcePositive)
    }
  }, [allowDecimals, onChange, value])
  const handlePlus = React.useCallback(() => {
    onChange(value + 1)
  }, [onChange, value])
  const handlePlusPointOne = React.useCallback(() => {
    onChange(value + 0.1)
  }, [onChange, value])

  const rootEl = (
    <rn.View
      style={(() => {
        if (!grow && !label) return hide ? styles.rootHidden : styles.root
        if (label) return styles.rootGrow
        return hide ? styles.rootGrowHidden : styles.rootGrow
      })()}
    >
      <rn.View style={gs.rowCentered}>
        <NumberInputButton
          disabled={disabled || manualInputEnabledVal}
          icon="minus"
          iconFamily="MaterialCommunityIcons"
          hidden={manualInputEnabledVal}
          on={on}
          onPress={handleMinus}
        />

        {gs.colGap8}

        {allowDecimals && !manualInputEnabledVal && (
          <rn.Pressable disabled={disabled} onPress={handleMinusPointOne}>
            <rn.View
              style={disabled ? styles.buttonSmallDisabled : styles.buttonSmall}
            >
              <rn.Text style={styles.buttonSmallText}>-.1</rn.Text>
            </rn.View>
          </rn.Pressable>
        )}
      </rn.View>

      <rn.Pressable
        disabled={disabled || manualInputEnabledVal}
        onPress={manualInputEnabledToggle}
        style={styles.textWrapper}
      >
        <NumberInputButton
          disabled={!manualInputEnabledVal}
          icon="rotate-ccw"
          iconFamily="Feather"
          hidden={!manualInputEnabledVal}
          on={on}
          onPress={manualInputDiscard}
          shadow
        />

        {manualInputEnabledVal && gs.colGap32}

        <rn.View style={styles.textInnerWrapper}>
          {manualInputEnabledVal ? (
            <rn.TextInput
              editable={manualInputEnabledVal}
              keyboardType={allowDecimals ? 'decimal-pad' : 'number-pad'}
              onChangeText={manualInputHandle}
              onSubmitEditing={manualInputConfirm}
              ref={manualInputRef}
              style={styles.textFocused}
              submitBehavior="blurAndSubmit"
              value={manualInputVal}
            />
          ) : (
            <rn.Text style={disabled ? styles.textDisabled : styles.text}>
              {value.toFixed(allowDecimals ? 2 : 0)}
            </rn.Text>
          )}

          {!manualInputEnabledVal && (
            <Feather
              color={t[on].highlight}
              name="edit-2"
              size={18}
              style={styles.editIcon}
            />
          )}
        </rn.View>

        {manualInputEnabledVal && gs.colGap32}

        <NumberInputButton
          disabled={!manualInputEnabledVal}
          icon="check"
          iconFamily="Feather"
          hidden={!manualInputEnabledVal}
          on={on}
          onPress={manualInputConfirm}
          shadow
        />
      </rn.Pressable>

      <rn.View style={gs.rowCentered}>
        {allowDecimals && !manualInputEnabledVal && (
          <rn.Pressable disabled={disabled} onPress={handlePlusPointOne}>
            <rn.View
              style={disabled ? styles.buttonSmallDisabled : styles.buttonSmall}
            >
              <rn.Text style={styles.buttonSmallText}>+.1</rn.Text>
            </rn.View>
          </rn.Pressable>
        )}

        {gs.colGap8}

        <NumberInputButton
          disabled={disabled || manualInputEnabledVal}
          icon="plus"
          iconFamily="MaterialCommunityIcons"
          hidden={manualInputEnabledVal}
          on={on}
          onPress={handlePlus}
        />
      </rn.View>
    </rn.View>
  )

  if (label) {
    return (
      <Labeled
        disabled={disabled}
        grow={grow}
        guideline={guideline}
        guidelineType={guideline ? guidelineType : 'warning'}
        hide={hide}
        label={label}
        on={on}
      >
        {rootEl}
      </Labeled>
    )
  }

  return rootEl
})

const TEXT_WIDTH = 96
const themedStyles = gs.ThemedStyleSheet.create((t, on, colorScheme) => {
  const flippedColorScheme = colorScheme === 'dark' ? 'light' : 'dark'

  const button = {
    alignItems: 'center' as const,
    alignSelf: 'center' as const,
    backgroundColor: c.themeTuple[flippedColorScheme][on].color,
    borderRadius: Number.MAX_SAFE_INTEGER,
    justifyContent: 'center' as const,
    padding: 12,
  }

  const buttonSmall = { ...button, padding: 6 }

  const text = {
    color: t[on].color,
    fontSize: 22,
    fontFamily: t[on].fontFamily,
    textAlign: 'center' as const,
    textAlignVertical: 'center' as const,
    width: TEXT_WIDTH,
    ...gs.userSelectNone,
  }

  return {
    button,
    buttonDisabled: { ...button, ...gs.disabled },
    buttonHidden: { ...button, ...gs.opacity0 },
    buttonShadow: { ...button, ...gs.shadowFull },
    buttonSmall,
    buttonSmallDisabled: { ...buttonSmall, ...gs.disabled },
    buttonSmallText: {
      ...gs.userSelectNone,
      color: t[on].color,
      fontSize: 22,
      textAlign: 'center' as const,
      textAlignVertical: 'center' as const,
    },
    editIcon: { ...gs.absolute, ...gs.deadCenter, right: -4 },
    rootGrow: { ...gs.inputGrowNew, ...gs.row, ...gs.rowSpaceBetween100 },
    rootGrowHidden: { ...gs.inputGrowNewHidden, ...gs.row },
    root: { ...gs.inputShrinkNew, ...gs.rowSpaceBetween },
    rootHidden: { ...gs.inputShrinkNewHidden, ...gs.row },
    text,
    textDisabled: { ...text, ...gs.disabled },
    textFocused: {
      ...text,
      ...gs.shadowHalf,
      backgroundColor: t.input[on].backgroundColor,
      borderColor: t.input[on].borderColor,
      borderRadius: t.input[on].borderRadius,
      borderWidth: t.input[on].borderWidth,
    },
    textInnerWrapper: { ...gs.deadCenter, width: TEXT_WIDTH },
    textWrapper: {
      ...gs.row,
      ...gs.deadCenter,
      // backgroundColor: '#CC00FF',
      paddingHorizontal: 24,
    },
  }
})

type NumberInputButtonProps = c.r<{
  disabled: boolean | undefined
  icon: string
  iconFamily: 'Feather' | 'MaterialCommunityIcons'
  hidden: boolean
  on: c.Surface
  onPress(): void
  shadow?: boolean
}>

const NumberInputButton = React.memo<NumberInputButtonProps>(
  function NumberInputButton({
    disabled,
    hidden,
    icon,
    iconFamily,
    on,
    onPress,
    shadow,
  }) {
    const styles = gs.useThemedStyleSheet(themedStyles, on)
    const _colorScheme = rn.useColorScheme()
    const colorScheme = _colorScheme === 'unspecified' ? 'light' : _colorScheme

    const IconComponent =
      iconFamily === 'Feather' ? Feather : MaterialCommunityIcons

    return (
      <rn.Pressable disabled={disabled} onPress={onPress}>
        <rn.View
          style={
            hidden
              ? styles.buttonHidden
              : disabled
              ? styles.buttonDisabled
              : shadow
              ? styles.buttonShadow
              : styles.button
          }
        >
          <IconComponent
            color={c.themeTuple[colorScheme][on].color as string}
            name={icon}
            size={20}
            style={disabled ? gs.disabled : gs.userSelectNone}
          />
        </rn.View>
      </rn.Pressable>
    )
  },
)
